import React from 'react';

interface RiskGaugeProps {
  score: number;        // 0-100
  previousScore?: number;
  size?: number;        // default 200
}

const RiskGauge: React.FC<RiskGaugeProps> = ({ score, previousScore, size = 200 }) => {
  const clampedScore = Math.min(100, Math.max(0, score));
  const radius = size * 0.35;
  const centerX = size / 2;
  const centerY = size * 0.55;
  const strokeWidth = size * 0.08;

  // Semi-circle from 180 degrees (left) to 0 degrees (right)
  const startAngle = Math.PI;
  const endAngle = 0;
  const scoreAngle = startAngle - (clampedScore / 100) * Math.PI;

  // Background arc path (full semi-circle)
  const bgStartX = centerX + radius * Math.cos(startAngle);
  const bgStartY = centerY - radius * Math.sin(startAngle);
  const bgEndX = centerX + radius * Math.cos(endAngle);
  const bgEndY = centerY - radius * Math.sin(endAngle);
  const bgPath = `M ${bgStartX} ${bgStartY} A ${radius} ${radius} 0 0 1 ${bgEndX} ${bgEndY}`;

  // Score arc path
  const scoreEndX = centerX + radius * Math.cos(scoreAngle);
  const scoreEndY = centerY - radius * Math.sin(scoreAngle);
  // The gauge is a half-circle, so the score arc never sweeps more than 180°.
  // The large-arc-flag must therefore always be 0 — setting it to 1 makes SVG
  // draw the *major* arc (the long way around), which loops the colored arc
  // outside the component bounds for any score > 50.
  const largeArc = 0;
  const scorePath = clampedScore > 0
    ? `M ${bgStartX} ${bgStartY} A ${radius} ${radius} 0 ${largeArc} 1 ${scoreEndX} ${scoreEndY}`
    : '';

  // Color based on score
  const getColor = (s: number) => {
    if (s <= 25) {return '#10B981';}  // Green
    if (s <= 50) {return '#F59E0B';}  // Yellow
    if (s <= 75) {return '#F97316';}  // Orange
    return '#EF4444';               // Red
  };

  const getLabel = (s: number) => {
    if (s <= 25) {return 'Low Risk';}
    if (s <= 50) {return 'Moderate';}
    if (s <= 75) {return 'Elevated';}
    return 'Critical';
  };

  const color = getColor(clampedScore);
  const label = getLabel(clampedScore);

  // Change from previous
  const change = previousScore !== undefined ? clampedScore - previousScore : 0;

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size * 0.65} viewBox={`0 0 ${size} ${size * 0.65}`}>
        {/* Background arc */}
        <path
          d={bgPath}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          className="dark:stroke-secondary-700"
        />
        {/* Score arc */}
        {scorePath && (
          <path
            d={scorePath}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
        )}
        {/* Score text */}
        <text
          x={centerX}
          y={centerY - size * 0.05}
          textAnchor="middle"
          className="fill-secondary-900 dark:fill-white"
          fontSize={size * 0.18}
          fontWeight="bold"
        >
          {clampedScore}
        </text>
        {/* Label text */}
        <text
          x={centerX}
          y={centerY + size * 0.08}
          textAnchor="middle"
          fill={color}
          fontSize={size * 0.07}
          fontWeight="600"
        >
          {label}
        </text>
      </svg>
      {/* Change indicator below SVG */}
      {change !== 0 && (
        <div className={`text-xs font-medium ${change > 0 ? 'text-danger-600 dark:text-danger-400' : 'text-success-600 dark:text-success-400'}`}>
          {change > 0 ? '+' : ''}{change} from previous period
        </div>
      )}
    </div>
  );
};

export default RiskGauge;
